import React, { useEffect, useMemo, useState, useRef } from 'react';
import API_URL from '../settings';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  IconButton,
  TextField,
  TablePagination,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import NavigationIcon from '@mui/icons-material/Navigation';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';

function Patients() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState([]); // holds parsed sheets
  const [importing, setImporting] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const fileInputRef = useRef(null);

  const fetchPatients = async () => {
    try {
      const url =
        user.role === 'VOLUNTEER'
          ? `${API_URL}/patients?volunteer_id=${user.id}`
          : `${API_URL}/patients?hospital_id=${user.hospital_id}`;

      const res = await fetch(url);
      const data = await res.json();

      setPatients(
        data.sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`
          )
        )
      );
    } catch (err) {
      console.error('Failed to fetch patients', err);
    }
  };

  /* ---------- Load patients ---------- */
  useEffect(() => {
    fetchPatients();
  }, [user]);

  /* ---------- Search ---------- */
  const filteredPatients = useMemo(() => {
    if (!search) return patients;
    return patients.filter(p =>
      `${p.citizen_id} ${p.first_name} ${p.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [patients, search]);

  /* ---------- Pagination ---------- */
  const paginatedPatients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPatients.slice(start, start + rowsPerPage);
  }, [filteredPatients, page, rowsPerPage]);

  const [healthRecordsMap, setHealthRecordsMap] = useState({});

  const fetchAllRecords = async () => {
    try {
      const res = await fetch(`${API_URL}/health_records`);
      const recordsData = await res.json();

      // 🧠 group records by patient_id (เอาเฉพาะที่มี patient_id)
      const map = {};
      recordsData
        .filter(r => r.patient_id)
        .forEach(r => {
          if (!map[r.patient_id]) map[r.patient_id] = [];
          map[r.patient_id].push(r);
        });

      // sort ล่าสุดก่อน
      Object.keys(map).forEach(pid => {
        map[pid].sort(
          (a, b) => new Date(b.checkup_date) - new Date(a.checkup_date)
        );
      });

      setHealthRecordsMap(map);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAllRecords();
  }, [user]);

  /* ---------- Health status ---------- */
  const renderHealthStatus = (p) => {
    const records = healthRecordsMap[p.id];

    if (!records || records.length === 0) {
      return <Chip label="ยังไม่ตรวจ" size="small" />;
    }

    const latest = records[0];
    const note = latest.notes?.trim();

    return (
      <Chip
        label={note || 'มีการตรวจ (ไม่มีหมายเหตุ)'}
        size="small"
        color={note ? 'primary' : 'default'}
        clickable
        onClick={() =>
          navigate(`/patients/${p.id}/health-records`)
        }
      />
    );
  };


  /* ---------- Export Excel ---------- */
  const handleExportExcel = () => {
    const data = filteredPatients.map(p => ({
      citizen_id: p.citizen_id,
      first_name: p.first_name,
      last_name: p.last_name,
      age: p.age,
      health_status: p.health_summary?.status || 'ยังไม่ตรวจ',
      last_checkup: p.health_summary?.last_checkup || '',
      income_status: p.economy?.status || '',
      welfare: p.economy?.welfare ? 'ใช่' : 'ไม่ใช่'
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Patients');

    XLSX.writeFile(
      workbook,
      `patients_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  /* ---------- Import Excel ---------- */
  const handleOpenImportDialog = () => {
    setImportData([]);
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadSample = () => {
    const workbook = XLSX.utils.book_new();

    // 1. Data Patient
    const dataPatientSheet = XLSX.utils.json_to_sheet([{
      Hosp_Name: "รพ.สต.ตัวอย่าง",
      Volunteer_Name: "ชื่ออาสาสมัคร",
      Pt_Full_Name: "สมหมาย ใจดี",
      ID_Card: "1234567890123",
      HN: "12345",
      Birth_Date: "1960-01-01",
      Age: 65,
      Gender: "M",
      Marital_Status: "สมรส",
      Economic_Status: "มี",
      Welfare: "ใช่",
      Occupation: "รับจ้าง",
      Income_Source: "ลูกหลาน",
      Blood_Group: "O",
      Rh: "+",
      Phone: "0812345678",
      Email: "sommai@email.com",
      Address_No: "12/3",
      Moo: "1",
      Soi: "",
      Road: "",
      Sub_District: "โพรงมะเดื่อ",
      District: "เมือง",
      Province: "นครปฐม",
      GPS_Lat: "13.823792",
      GPS_Long: "99.989377",
      Living_Condition: "ไม่อยู่คนเดียว",
      Weight: 65,
      Height: 165,
      Congenital_Disease: "ความดัน",
      Surgery_History: "ไม่มี",
      Allergy_History: "ไม่มี",
      Family_Disease: "ไม่มี",
      Vulnerable_Group: "ไม่ระบุ",
      Risk_Factors: "ไม่ระบุ",
      Care_Name: "สมศรี ใจดี",
      Care_Phone: "0898765432",
      Care_Relationship: "บุตร"
    }]);
    XLSX.utils.book_append_sheet(workbook, dataPatientSheet, 'Data Patient');

    // 2. ADL Record
    const adlSheet = XLSX.utils.json_to_sheet([{
      ID_Card: "1234567890123",
      feeding: 2,
      grooming: 1,
      transfer: 3,
      toilet_use: 2,
      mobility: 3,
      dressing: 2,
      stairs: 2,
      bathing: 1,
      bowels: 2,
      bladder: 2,
      total_score: 20,
      adl_group: "ติดสังคม"
    }]);
    XLSX.utils.book_append_sheet(workbook, adlSheet, 'ADL Record');

    // 3. depression
    const dep2QSheet = XLSX.utils.json_to_sheet([{
      ID_Card: "1234567890123",
      answer2q_1: "false",
      answer2q_2: "false",
      result_2q: "false"
    }]);
    XLSX.utils.book_append_sheet(workbook, dep2QSheet, 'depression');

    // 4. depression_ต่อ
    const dep9QSheet = XLSX.utils.json_to_sheet([{
      ID_Card: "1234567890123",
      answer9q_1: 0,
      answer9q_2: 0,
      answer9q_3: 0,
      answer9q_4: 0,
      answer9q_5: 0,
      answer9q_6: 0,
      answer9q_7: 0,
      answer9q_8: 0,
      answer9q_9: 0,
      result_9q_score: 0
    }]);
    XLSX.utils.book_append_sheet(workbook, dep9QSheet, 'depression_ต่อ');

    // 5. ความเครียด
    const stressSheet = XLSX.utils.json_to_sheet([{
      ID_Card: "1234567890123",
      answer_st5_1: 0,
      answer_st5_2: 0,
      answer_st5_3: 0,
      answer_st5_4: 0,
      answer_st5_5: 0,
      result_st5_score: 0
    }]);
    XLSX.utils.book_append_sheet(workbook, stressSheet, 'ความเครียด');

    XLSX.writeFile(workbook, `patient_import_template_5tabs.xlsx`);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        
        const getSheetData = (name) => {
            const sheet = wb.Sheets[name] || wb.Sheets[wb.SheetNames.find(n => n.includes(name))];
            return sheet ? XLSX.utils.sheet_to_json(sheet) : [];
        };

        const dataPatient = getSheetData('Data Patient');
        const adlRecord = getSheetData('ADL');
        const depression2Q = getSheetData('depression');
        const depression9Q = getSheetData('ต่อ');
        const stress = getSheetData('ความเครียด');

        setImportData({
            patients: dataPatient,
            adl: adlRecord,
            dep2q: depression2Q,
            dep9q: depression9Q,
            stress: stress
        });
      } catch (error) {
        setToast({ open: true, message: 'เกิดข้อผิดพลาดในการอ่านไฟล์ Excel', severity: 'error' });
      }
    };
    reader.readAsBinaryString(file);
  };

  const calculate9QResult = (score) => {
    let label = "ไม่มีอาการของโรคซึมเศร้า";
    if (score >= 7 && score <= 12) label = "มีอาการของโรคซึมเศร้า ระดับน้อย";
    else if (score >= 13 && score <= 18) label = "มีอาการของโรคซึมเศร้า ระดับปานกลาง";
    else if (score >= 19) label = "มีอาการของโรคซึมเศร้า ระดับรุนแรง";
    return label;
  };

  const calculateST5Result = (score) => {
    let label = "ความเครียดน้อย";
    if (score >= 5 && score <= 7) label = "ความเครียดปานกลาง";
    else if (score >= 8 && score <= 9) label = "ความเครียดมาก";
    else if (score >= 10) label = "ความเครียดมากที่สุด";
    return label;
  };

  const handleConfirmImport = async () => {
    if (!importData.patients || importData.patients.length === 0) return;
    setImporting(true);

    let successCount = 0;
    let failCount = 0;

    // Fetch existing volunteers to match Volunteer_Name
    let existingVolunteers = [];
    try {
        const vRes = await fetch(`${API_URL}/users?role=VOLUNTEER`);
        existingVolunteers = await vRes.json();
    } catch(e) {}

    for (const row of importData.patients) {
      try {
        const citizenIdStr = row.ID_Card ? String(row.ID_Card) : null;
        if (!citizenIdStr) {
            failCount++;
            continue;
        }

        // Split Pt_Full_Name into first and last name
        const names = row.Pt_Full_Name ? row.Pt_Full_Name.split(' ') : ['', ''];
        const firstName = names[0] || '';
        const lastName = names.slice(1).join(' ') || '';

        // Match volunteer
        let volunteerId = null;
        if (row.Volunteer_Name) {
            const vol = existingVolunteers.find(v => v.name && v.name.includes(row.Volunteer_Name));
            if (vol) volunteerId = vol.id;
        }

        // Find related data from other tabs
        const adlRow = importData.adl?.find(r => String(r.ID_Card) === citizenIdStr) || {};
        const dep2qRow = importData.dep2q?.find(r => String(r.ID_Card) === citizenIdStr) || {};
        const dep9qRow = importData.dep9q?.find(r => String(r.ID_Card) === citizenIdStr) || {};
        const stressRow = importData.stress?.find(r => String(r.ID_Card) === citizenIdStr) || {};

        const dep9qScore = Number(dep9qRow.result_9q_score || 0);
        const stressScore = Number(stressRow.result_st5_score || 0);

        const payload = {
          citizen_id: citizenIdStr,
          hn: row.HN ? String(row.HN) : null,
          first_name: firstName,
          last_name: lastName,
          gender: row.Gender || 'M',
          birth_date: row.Birth_Date || null,
          age: row.Age ? Number(row.Age) : null,
          phone: row.Phone ? String(row.Phone) : null,
          email: row.Email || null,
          
          patient_type: "ELDERLY",
          marital_status: row.Marital_Status || null,
          is_elderly: true,
          living_alone: row.Living_Condition && row.Living_Condition.includes('คนเดียว'),
          
          blood_type: row.Blood_Group || null,
          rh_factor: row.Rh || null,
          weight_kg: row.Weight ? Number(row.Weight) : null,
          height_cm: row.Height ? Number(row.Height) : null,
          surgery_history: row.Surgery_History || null,
          allergy: row.Allergy_History || null,
          family_disease_history: row.Family_Disease || null,
          chronic_disease_note: row.Congenital_Disease || null,
          
          volunteer_id: volunteerId,
          
          care_first_name: row.Care_Name ? row.Care_Name.split(' ')[0] : null,
          care_last_name: row.Care_Name ? row.Care_Name.split(' ').slice(1).join(' ') : null,
          care_phone: row.Care_Phone ? String(row.Care_Phone) : null,
          care_related: row.Care_Relationship || null,

          address_detail: {
            house_no: row.Address_No || null,
            village: row.Moo || null,
            alley: row.Soi || null,
            road: row.Road || null,
            subdistrict: row.Sub_District || null,
            district: row.District || null,
            province: row.Province || null,
            lat: row.GPS_Lat ? Number(row.GPS_Lat) : null,
            lng: row.GPS_Long ? Number(row.GPS_Long) : null,
          },
          economy: {
            status: row.Economic_Status || null,
            occupation: row.Occupation || null,
            source: row.Income_Source || null,
            welfare: row.Welfare === 'ใช่' || row.Welfare === true || row.Welfare === '1',
          },
          
          depression: {
             answer2q_1: dep2qRow.answer2q_1 === 'true' || dep2qRow.answer2q_1 === true,
             answer2q_2: dep2qRow.answer2q_2 === 'true' || dep2qRow.answer2q_2 === true,
             result_2q: dep2qRow.result_2q === 'true' || dep2qRow.result_2q === true,
             answer9q_1: Number(dep9qRow.answer9q_1 || 0),
             answer9q_2: Number(dep9qRow.answer9q_2 || 0),
             answer9q_3: Number(dep9qRow.answer9q_3 || 0),
             answer9q_4: Number(dep9qRow.answer9q_4 || 0),
             answer9q_5: Number(dep9qRow.answer9q_5 || 0),
             answer9q_6: Number(dep9qRow.answer9q_6 || 0),
             answer9q_7: Number(dep9qRow.answer9q_7 || 0),
             answer9q_8: Number(dep9qRow.answer9q_8 || 0),
             answer9q_9: Number(dep9qRow.answer9q_9 || 0),
             result_9q_score: dep9qScore,
             result_9q_label: calculate9QResult(dep9qScore),
             assessed_date: new Date().toISOString().split("T")[0],
          },

          stress: {
             answer_st5_1: Number(stressRow.answer_st5_1 || 0),
             answer_st5_2: Number(stressRow.answer_st5_2 || 0),
             answer_st5_3: Number(stressRow.answer_st5_3 || 0),
             answer_st5_4: Number(stressRow.answer_st5_4 || 0),
             answer_st5_5: Number(stressRow.answer_st5_5 || 0),
             result_st5_score: stressScore,
             result_st5_label: calculateST5Result(stressScore),
             assessed_date: new Date().toISOString().split("T")[0],
          },

          hospital_id: user.hospital_id || 'HSP01',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const res = await fetch(`${API_URL}/patients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
           const createdPatient = await res.json();
           
           // Post ADL Record if ADL tab data exists
           if (Object.keys(adlRow).length > 0 && adlRow.total_score !== undefined) {
               await fetch(`${API_URL}/adl_records`, {
                   method: 'POST',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({
                       patient_id: createdPatient.id,
                       assessment_date: new Date().toISOString().split('T')[0],
                       feeding: Number(adlRow.feeding || 0),
                       grooming: Number(adlRow.grooming || 0),
                       transfer: Number(adlRow.transfer || 0),
                       toilet_use: Number(adlRow.toilet_use || 0),
                       mobility: Number(adlRow.mobility || 0),
                       dressing: Number(adlRow.dressing || 0),
                       stairs: Number(adlRow.stairs || 0),
                       bathing: Number(adlRow.bathing || 0),
                       bowels: Number(adlRow.bowels || 0),
                       bladder: Number(adlRow.bladder || 0),
                       total_score: Number(adlRow.total_score || 0),
                       adl_group: adlRow.adl_group || "ไม่มีข้อมูล",
                       created_at: new Date().toISOString()
                   })
               });
           }
           
           successCount++;
        } else {
           failCount++;
        }
      } catch (err) {
        console.error("Error importing row", row, err);
        failCount++;
      }
    }

    setToast({
      open: true,
      message: `นำเข้าสำเร็จ ${successCount} รายการ, ล้มเหลว ${failCount} รายการ`,
      severity: failCount > 0 ? 'warning' : 'success'
    });
    
    setImporting(false);
    handleCloseImportDialog();
    fetchPatients();
  };

  /* ---------- Google Map ---------- */
  const handleNavigate = (lat, lng) => {
    if (!lat || !lng) return;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  return (
    <>
      {/* ===== Header ===== */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
          ข้อมูลผู้สูงอายุ
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="ค้นหาเลขบัตร / ชื่อ"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
          >
            Export Excel
          </Button>

          {user.role !== 'VOLUNTEER' && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenImportDialog}
            >
              นำเข้า Excel
            </Button>
          )}

          {user.role !== 'VOLUNTEER' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/patients/new')}
            >
              เพิ่มข้อมูลผู้สูงอายุ
            </Button>
          )}
        </Box>
      </Box>

      {/* ===== Table ===== */}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ชื่อ-สกุล</TableCell>
                <TableCell>เลขบัตรประชาชน</TableCell>
                <TableCell align="right">อายุ</TableCell>
                <TableCell>สุขภาพล่าสุด</TableCell>
                <TableCell>รายได้</TableCell>
                <TableCell align="center">นำทาง</TableCell>
                <TableCell align="center">สุขภาพ</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedPatients.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={p.patient_image} alt={p.first_name}>
                        {!p.patient_image && p.first_name ? p.first_name[0] : null}
                      </Avatar>
                      <Typography>
                        {p.first_name} {p.last_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{p.citizen_id}</TableCell>
                  <TableCell align="right">{p.age || '-'}</TableCell>

                  <TableCell>
                    {renderHealthStatus(p)}
                  </TableCell>

                  <TableCell>{p.economy?.status || '-'}</TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleNavigate(
                          p.address_detail?.lat,
                          p.address_detail?.lng
                        )
                      }
                      disabled={!p.address_detail?.lat}
                    >
                      <NavigationIcon fontSize="small" />
                    </IconButton>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        navigate(`/patients/${p.id}/health-records`)
                      }
                    >
                      <MedicalInformationIcon fontSize="small" />
                    </IconButton>
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/patients/${p.id}`)}
                    >
                      ดูข้อมูล
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredPatients.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
      
      {/* ===== Import Dialog ===== */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>นำเข้าข้อมูลผู้สูงอายุ (Excel)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSample}
            >
              ดาวน์โหลดไฟล์ตัวอย่าง (5 Tabs)
            </Button>
            
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              เลือกไฟล์ Excel
              <input
                type="file"
                accept=".xlsx, .xls"
                hidden
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
            </Button>
          </Box>

          {importData.patients && importData.patients.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                ตัวอย่างข้อมูลที่จะนำเข้า ({importData.patients.length} รายการ)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>เลขบัตรฯ</TableCell>
                      <TableCell>ชื่อ-สกุล</TableCell>
                      <TableCell>อายุ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importData.patients.slice(0, 5).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.ID_Card}</TableCell>
                        <TableCell>{row.Pt_Full_Name}</TableCell>
                        <TableCell>{row.Age}</TableCell>
                      </TableRow>
                    ))}
                    {importData.patients.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          ... และอีก {importData.patients.length - 5} รายการ
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>ยกเลิก</Button>
          <Button 
            onClick={handleConfirmImport} 
            variant="contained" 
            color="primary"
            disabled={!importData.patients || importData.patients.length === 0 || importing}
            startIcon={importing ? <CircularProgress size={20} /> : null}
          >
            {importing ? 'กำลังนำเข้า...' : 'ยืนยันการนำเข้า'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Patients;
